import * as api from './countriesApi';
import { ConfirmationModal, ModalProvider, useModal } from '../generalcomponents/ModalProvider';
import type { Country, Vat } from './countriesApi';
import { DetailViewShell, FormRow, mergeByKey, sx13 } from '../common/detailView';
import { Stack, Tab, Table, TableBody, TableCell, TableRow, Tabs, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDetailView } from '../common/useDetailView';
import { useTranslation } from 'react-i18next';

interface Props {
    countryId: number | null;
}

export function CountryView(props: Props) {
    return (
        <ModalProvider>
            <CountryViewInner {...props} />
        </ModalProvider>
    );
}

function CountryViewInner({ countryId }: Props) {
    const { t } = useTranslation();
    const { openModal } = useModal();
    const detail = useDetailView<Country>();
    const [vats, setVats] = useState<Vat[]>([]);
    const [tab, setTab] = useState(0);

    const mergeVats = (existing: Country['vats'], all: Vat[]) => mergeByKey(existing, all, (cv) => cv.vat.id, (vat) => ({ vat, vatRate: vat.vatRate }));

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const vatsData = await api.getVats();
            setVats(vatsData);
            if (countryId === null) {
                detail.setItem({ id: 0, code: '', name: '', vats: mergeVats([], vatsData) });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getCountry(countryId);
                data.vats = mergeVats(data.vats ?? [], vatsData);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [countryId]);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!detail.item) return;
        await detail.withSaving(async () => {
            const response = detail.isNew ?
                await api.createCountry(detail.item!) :
                await api.updateCountry(detail.item!);
            response.vats = mergeVats(response.vats ?? [], vats);
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    const del = () => {
        if (!detail.item) return;
        openModal(
            <ConfirmationModal
                header={t('common.delete')}
                message={t('country.confirmDelete')}
                onModalResponse={(confirmed) => { if (confirmed) performDelete(); }}
            />
        );
    };

    const performDelete = async () => {
        if (!detail.item) return;
        await detail.withSaving(async () => {
            await api.deleteCountry(detail.item!.id);
            window.history.back();
        });
    };

    return (
        <DetailViewShell<Country>
            featureKey="devFeature_reactCountryView"
            title={`${detail.item?.code ?? ''} ${detail.item?.name ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave
            loading={detail.loading}
            error={detail.error}
            item={detail.item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onDelete={del}
            onClearError={() => detail.setError(null)}
        >
            {(item) => {
                const updateVatRate = (vatId: number, vatRate: number) => {
                    detail.update('vats', item.vats.map((cv) => (cv.vat.id === vatId ? { ...cv, vatRate } : cv)
                    ));
                };

                const sortedVats = [...item.vats].sort((a, b) => (a.vat.vatCode ?? '').localeCompare(b.vat.vatCode ?? '')
                );

                return (<>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                        <Tab sx={sx13} label={t('role.country')} />
                        <Tab sx={sx13} label={t('country.vat')} />
                    </Tabs>

                    {tab === 0 && (
                        <Stack spacing={2}>
                            <FormRow label={t('country.name')}>
                                {!detail.isEditing ? (
                                    <Typography sx={sx13}>{item.name}</Typography>
                                ) : (
                                    <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                        value={item.name} onChange={(e) => detail.update('name', e.target.value)} />
                                )}
                            </FormRow>

                            <FormRow label={t('country.code')}>
                                {!detail.isNew && !detail.isEditing ? (
                                    <Typography sx={sx13}>{item.code}</Typography>
                                ) : (
                                    <TextField size="small" fullWidth
                                        inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
                                        value={item.code}
                                        onChange={(e) => detail.update('code', e.target.value.toUpperCase())} />
                                )}
                            </FormRow>
                        </Stack>
                    )}

                    {tab === 1 && (
                        <Table size="small">
                            <TableBody>
                                {sortedVats.map((cv) => (
                                    <TableRow key={cv.vat.id}>
                                        <TableCell sx={{ ...sx13, width: 300 }}>
                                            {cv.vat.vatCode} {cv.vat.vatName}
                                        </TableCell>
                                        <TableCell>
                                            {!detail.isEditing ? (
                                                <Typography sx={sx13}>{cv.vatRate}</Typography>
                                            ) : (
                                                <TextField size="small" type="number" fullWidth
                                                    value={cv.vatRate ?? 0}
                                                    onChange={(e) => updateVatRate(cv.vat.id, e.target.value === '' ? 0 : Number(e.target.value))} />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </>);
            }}
        </DetailViewShell>
    );
}
